import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import axios from 'axios'
import to from 'await-to-js'
import Checkbox from 'components/Checkbox/checkbox'
import { ClassicEditor } from 'components/Editor'
import FbButton from 'components/FbUI/FbButton'
import { useMemo, useState } from 'react'
import { toast } from 'react-toastify'
import useApiService from 'services/api'
import { cn } from 'components/UI/cn'
import { EmailSignature } from 'models/emailIntegration'
import DOMPurify from 'dompurify'
import { PiPencilSimple, PiPlus, PiTrash } from 'react-icons/pi'
import dialogConfirm from 'components/dialogConfirm'
import { OverflownText } from 'components/OverflownText'

const SIGNATURES_KEY = ['email-signatures']

// KEEP IN SYNC with the backend constant
// MAX_SIGNATURE_HTML_BYTES in
// server/email_integration/services/signature_sanitizer.py
const MAX_HTML_BYTES = 8 * 1024
const _utf8 = new TextEncoder()
const htmlByteLength = (s: string): number => _utf8.encode(s).byteLength

const CARD_SHADOW =
  'shadow-[0px_1px_3px_0px_rgba(16,24,40,0.08),0px_1px_2px_-1px_rgba(16,24,40,0.04)]'

export function EmailIntegrationSignatures() {
  const api = useApiService()
  const queryClient = useQueryClient()
  const [adding, setAdding] = useState(false)
  const [editingId, setEditingId] = useState<number | null>(null)

  const { data, isFetching } = useQuery({
    queryKey: SIGNATURES_KEY,
    queryFn: async ({ signal }) => {
      const [err, res] = await to(
        api.emailIntegration.getEmailSignatureList(signal)
      )
      if (err) throw err
      return res
    },
  })

  const signatures = data?.results ?? []

  const handleSaved = () => {
    setAdding(false)
    setEditingId(null)
    queryClient.invalidateQueries({ queryKey: SIGNATURES_KEY })
  }

  const descriptionText =
    'Add and manage your signatures. You can have multiple — set one as the default.'

  return (
    <div className="flex flex-col gap-4 w-full overflow-y-auto bg-surface-page p-1">
      <div className="flex items-start justify-between gap-4">
        <OverflownText
          tooltipText={descriptionText}
          maxLines={4}
          placement="bottom"
          className="text-sm text-gray-600"
        >
          {descriptionText}
        </OverflownText>

        {!adding && (
          <FbButton
            variant="primary"
            size="sm"
            onClick={() => {
              setAdding(true)
              setEditingId(null)
            }}
            className="!bg-brand-bright !text-white hover:!bg-brand-active shrink-0"
          >
            <PiPlus size={16} />
            Add Signature
          </FbButton>
        )}
      </div>

      {adding && (
        <SignatureEditor
          onCancel={() => setAdding(false)}
          onSaved={handleSaved}
        />
      )}

      {isFetching && signatures.length === 0 ? (
        <div className="text-sm text-gray-500">Loading signatures…</div>
      ) : signatures.length === 0 && !adding ? (
        <div
          className={cn(
            'rounded-lg border border-gray-200 bg-white p-4 text-sm italic text-gray-500',
            CARD_SHADOW
          )}
        >
          You don&apos;t have any signatures yet.
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          {signatures.map((sig) =>
            editingId === sig.id ? (
              <SignatureEditor
                key={sig.id}
                signature={sig}
                onCancel={() => setEditingId(null)}
                onSaved={handleSaved}
              />
            ) : (
              <SignatureItem
                key={sig.id}
                signature={sig}
                onEdit={() => setEditingId(sig.id)}
              />
            )
          )}
        </div>
      )}
    </div>
  )
}

function SignatureItem({
  signature,
  onEdit,
}: {
  signature: EmailSignature
  onEdit: () => void
}) {
  const api = useApiService()
  const queryClient = useQueryClient()
  // Keep inline styles + style attributes so the preview matches what the
  // recipient actually sees (DOMPurify strips them by default). The server
  // already sanitized via justhtml on save.
  const sanitized = useMemo(
    () => DOMPurify.sanitize(signature.html, { ADD_ATTR: ['style'] }),
    [signature.html]
  )

  const setDefaultMutation = useMutation({
    mutationFn: async () => {
      const [err] = await to(
        api.emailIntegration.setDefaultEmailSignature(signature.id)
      )
      if (err) throw err
    },
    onSuccess: () => {
      toast.success('Set as default')
      queryClient.invalidateQueries({ queryKey: SIGNATURES_KEY })
    },
    onError: () => toast.error('Failed to set default'),
  })

  const deleteMutation = useMutation({
    mutationFn: async () => {
      const [err] = await to(
        api.emailIntegration.deleteEmailSignature(signature.id)
      )
      if (err) throw err
    },
    onSuccess: () => {
      toast.success('Signature deleted')
      queryClient.invalidateQueries({ queryKey: SIGNATURES_KEY })
    },
    onError: () => toast.error('Failed to delete'),
  })

  const handleDelete = async () => {
    const ok = await dialogConfirm(
      signature.is_default
        ? 'This is the default signature. Another will be promoted automatically.'
        : 'This signature will be removed.',
      'Delete signature?',
      '',
      { confirmLabel: 'Delete', buttonVariant: 'danger' }
    )
    if (ok) deleteMutation.mutate()
  }

  return (
    <div
      className={cn(
        'rounded-lg border border-gray-200 bg-white p-4 flex flex-col gap-3',
        CARD_SHADOW
      )}
    >
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-sm font-medium text-gray-800 truncate">
            {signature.name || 'Untitled'}
          </span>
          {signature.is_default && (
            <span className="inline-flex items-center rounded-full bg-[#dcfce7] px-2 py-0.5 text-xs font-medium text-[#15803d] tracking-[-0.2px]">
              Default
            </span>
          )}
        </div>
        <div className="flex items-center gap-1">
          {!signature.is_default && (
            <button
              type="button"
              onClick={() => setDefaultMutation.mutate()}
              disabled={setDefaultMutation.isPending}
              className="text-xs text-brand-active hover:text-brand-bright underline-offset-2 hover:underline px-2 py-1 disabled:opacity-50"
              title="Set as default"
            >
              Set default
            </button>
          )}
          <button
            type="button"
            onClick={onEdit}
            className="p-1.5 rounded-md text-gray-500 hover:bg-gray-100 hover:text-gray-800"
            title="Edit"
            aria-label="Edit signature"
          >
            <PiPencilSimple size={16} />
          </button>
          <button
            type="button"
            onClick={handleDelete}
            disabled={deleteMutation.isPending}
            className="p-1.5 rounded-md text-gray-500 hover:bg-gray-100 hover:text-red-600 disabled:opacity-50"
            title="Delete"
            aria-label="Delete signature"
          >
            <PiTrash size={16} />
          </button>
        </div>
      </div>

      <div
        className="border border-gray-200 rounded-lg p-3 bg-gray-50 text-sm overflow-auto"
        dangerouslySetInnerHTML={{ __html: sanitized }}
      />
    </div>
  )
}

function SignatureEditor({
  signature,
  onCancel,
  onSaved,
}: {
  signature?: EmailSignature
  onCancel: () => void
  onSaved: () => void
}) {
  const api = useApiService()
  const isEditing = !!signature

  const [name, setName] = useState(signature?.name ?? '')
  const [html, setHtml] = useState(signature?.html ?? '')
  const [isDefault, setIsDefault] = useState(signature?.is_default ?? false)
  const htmlBytes = useMemo(() => htmlByteLength(html), [html])
  const isOverByteLimit = htmlBytes > MAX_HTML_BYTES

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (isEditing && signature) {
        const [err] = await to(
          api.emailIntegration.updateEmailSignature(signature.id, {
            name,
            html,
            ...(isDefault && !signature.is_default ? { is_default: true } : {}),
          })
        )
        if (err) throw err
      } else {
        const [err] = await to(
          api.emailIntegration.createEmailSignature({
            name,
            html,
            is_default: isDefault,
          })
        )
        if (err) throw err
      }
    },
    onSuccess: () => {
      toast.success(isEditing ? 'Signature saved' : 'Signature created')
      onSaved()
    },
    onError: (err) => {
      const detail = axios.isAxiosError<{
        detail?: string
        html?: string | string[]
      }>(err)
        ? (err.response?.data?.detail ??
          [err.response?.data?.html].flat().filter(Boolean).join(' '))
        : undefined
      toast.error(detail || 'Failed to save signature')
    },
  })

  return (
    <div
      className={cn(
        'rounded-lg border border-gray-200 bg-white p-4 flex flex-col gap-3',
        CARD_SHADOW
      )}
    >
      <div className="text-sm font-medium text-gray-800">
        {isEditing ? 'Edit signature' : 'New signature'}
      </div>
      <label className="flex flex-col gap-1.5 text-xs font-medium text-gray-700">
        Label
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Default, Outreach, Internal"
          className="w-full h-11 px-4 text-sm bg-white border border-gray-300 rounded-lg text-gray-800 placeholder:text-gray-400 focus:outline-none focus:border-brand-active focus:shadow-[0_0_0_4px_rgba(8,116,67,0.2)]"
        />
      </label>
      <div className="flex flex-col gap-1.5 text-xs font-medium text-gray-700">
        <span>Signature</span>
        <ClassicEditor
          value={html}
          onChange={(next) => setHtml(next ?? '')}
          placeholder="Sarah Chen — Senior Account Executive · Acme"
          className={cn(
            'min-h-[140px] rounded-lg border',
            isOverByteLimit ? 'border-red-500' : 'border-gray-300'
          )}
        />
      </div>
      <div
        className={cn(
          'flex items-center justify-between text-xs',
          isOverByteLimit ? 'text-red-600' : 'text-gray-500'
        )}
      >
        <span>
          {isOverByteLimit
            ? `Signature exceeds the ${MAX_HTML_BYTES / 1024} KB limit. Trim it before saving.`
            : ''}
        </span>
        <span aria-live="polite">
          {(htmlBytes / 1024).toFixed(1)} / {MAX_HTML_BYTES / 1024} KB
        </span>
      </div>

      <div className="flex items-center justify-between gap-3 pt-1">
        <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
          <Checkbox
            checked={isDefault}
            disabled={signature?.is_default}
            onChange={(e) => setIsDefault(e.currentTarget.checked)}
          />
          <span>Set as default</span>
        </label>
        <div className="flex items-center gap-2">
          <FbButton
            variant="secondary"
            size="sm"
            onClick={onCancel}
            disabled={saveMutation.isPending}
          >
            Cancel
          </FbButton>
          <FbButton
            variant="primary"
            size="sm"
            onClick={() => saveMutation.mutate()}
            disabled={
              saveMutation.isPending || html.trim() === '' || isOverByteLimit
            }
            className="!bg-brand-bright !text-white hover:!bg-brand-active"
          >
            {saveMutation.isPending ? 'Saving…' : isEditing ? 'Save' : 'Create'}
          </FbButton>
        </div>
      </div>
    </div>
  )
}
